/**
 * One letter, held at the lips, going nowhere.
 *
 * Everything else on this page moves, and that is exactly what makes the anchor
 * impossible to check. A word thrown from slightly the wrong place still leaves
 * the mouth in roughly the right direction and is gone in two seconds; a whole
 * phase of this project launched its words from somewhere near the forehead and
 * nothing on screen could say so, because "near the face" is indistinguishable
 * from a bad landmark index. Stopping one glyph on the lip centroid turns a
 * geometry bug back into something a person can see.
 *
 * The crosshair is the measurement. The glyph beside it is a second question
 * answered by the same object: whether aperture and width are actually reaching
 * the type, live, before any of it has moved. It goes through
 * `variationSettings` — the same mapping `charElement` uses — at the same size
 * `Emitter.emit` launches a glyph at, because a preview that computed its own
 * size would be a preview of nothing.
 *
 * Position is transform and nothing else, like the emitter. The two size writes
 * are guarded on having actually changed, so a still face costs no style
 * invalidation at all.
 */

import { gradFor, variationSettings } from './caption.js';
import { glyphSize, origin } from './emitter.js';

const SVG_NS = 'http://www.w3.org/2000/svg';

/**
 * The launch point, drawn as a ring rather than a cross.
 *
 * Two different questions want two different marks. The cross asks "is the
 * anchor on my lips", which is a point. This asks "where will the type come
 * out", which is an area — the dashed circle is the scatter `emit.JITTER` adds,
 * so what is drawn is the region letters actually leave from rather than an
 * idealised centre they mostly miss.
 */
function launchRing() {
  const svg = document.createElementNS(SVG_NS, 'svg');
  svg.setAttribute('viewBox', '-50 -50 100 100');
  svg.setAttribute('class', 'probe-emitter');
  svg.setAttribute('aria-hidden', 'true');

  const scatter = document.createElementNS(SVG_NS, 'circle');
  scatter.setAttribute('class', 'probe-scatter');
  scatter.setAttribute('r', '48');
  const dot = document.createElementNS(SVG_NS, 'circle');
  dot.setAttribute('class', 'probe-launch');
  dot.setAttribute('r', '5');
  svg.append(scatter, dot);
  return { svg, scatter, dot };
}

/**
 * The crosshair, in a viewBox centred on the anchor.
 *
 * Four ticks with a hole in the middle rather than a full cross or a dot: the
 * thing being aimed at is the lip opening, and a mark that covers it is a mark
 * you cannot check. The gap is the target.
 */
function crosshair() {
  const svg = document.createElementNS(SVG_NS, 'svg');
  svg.setAttribute('viewBox', '-10 -10 20 20');
  svg.setAttribute('class', 'probe-cross');
  svg.setAttribute('aria-hidden', 'true');
  for (const d of ['M-10 0H-3', 'M3 0H10', 'M0 -10V-3', 'M0 3V10']) {
    const path = document.createElementNS(SVG_NS, 'path');
    path.setAttribute('d', d);
    svg.append(path);
  }
  return svg;
}

export class Probe {
  /**
   * @param {HTMLElement} el  the probe element, already inside the layer the
   *   anchor is measured in — sharing that layer with the emitter is what makes
   *   it impossible for the two to disagree about where the mouth is
   * @param {object} [o]
   * @param {object} [o.config] the whole config, read at use time. This needs
   *   both `probe` and `emit.SCALE_FROM`, and holding either by value would
   *   produce a slider that moves and does nothing.
   */
  constructor(el, { config } = {}) {
    this.el = el;
    this._config = config;

    // Two markers, each hung off the anchor by its own offset rather than off
    // each other. They answer different questions and are moved by different
    // controls, and chaining them would make `probe.X` quietly drag the launch
    // ring around with it.
    this.mark = document.createElement('div');
    this.mark.className = 'probe-mark';
    this.cross = crosshair();
    this.glyph = document.createElement('span');
    this.glyph.className = 'probe-glyph cap-char';
    this.note = document.createElement('span');
    this.note.className = 'probe-note mono';
    this.mark.append(this.cross, this.glyph, this.note);

    const ring = launchRing();
    this.emitter = ring.svg;
    this.scatter = ring.scatter;
    this.launch = ring.dot;

    el.replaceChildren(this.mark, this.emitter);

    this._letter = '';
    this._axes = '';
    this._size = 0;
    this._type = 0;
    this._adrift = null;
    this._showCross = null;
    this._showGlyph = null;
    this._showEmitter = null;
    // -1 rather than 0, so the first frame always writes. A jitter of exactly 0
    // is a legitimate setting and would otherwise match the initial value and
    // skip the write that hides the scatter circle.
    this._ring = -1;
  }

  get cfg() {
    return this._config;
  }

  /**
   * Somewhere to be before there is a face.
   *
   * A probe that hides when it has nothing to point at is indistinguishable from
   * a probe that was never wired up — which is exactly the half hour this cost
   * the first time. So it parks in the middle of its own layer instead, marked as
   * adrift, and says why. "In the centre saying no face" and "on the lips" are
   * two different, readable states; "absent" is neither.
   */
  _centre() {
    const layer = this.el.parentElement;
    const w = layer?.clientWidth || 0;
    const h = layer?.clientHeight || 0;
    // A nominal scale so the thing has a size at all. Roughly a face at arm's
    // length, and it is replaced by the real iod the moment one is seen.
    return { x: w / 2, y: h / 2, iod: Math.max(48, h * 0.12) };
  }

  /**
   * Park the probe and shape it from the mouth right now.
   *
   * @param {?{x: number, y: number, iod: number}} at  null when there is no face
   * @param {?object} [sample] the latest MouthSample
   * @param {?object} [audioFrame] the latest AudioFrame, for GRAD
   */
  frame(at, sample = null, audioFrame = null) {
    const cfg = this.cfg;
    const showCross = cfg.probe.ON && cfg.probe.CROSS;
    const showGlyph = cfg.probe.ON && cfg.probe.GLYPH;
    const showEmitter = cfg.probe.ON && cfg.probe.EMITTER;

    // Every part off is the same as off, whatever the master switch says — so
    // turning the last one off clears the screen rather than leaving a lone
    // `no face` caption behind with nothing to caption.
    if (!showCross && !showGlyph && !showEmitter) {
      this.el.hidden = true;
      return;
    }
    this.el.hidden = false;

    // `display` rather than the `hidden` attribute: these are SVG elements, and
    // `hidden` is an HTML attribute that does not reflect onto one.
    if (showCross !== this._showCross) {
      this._showCross = showCross;
      this.cross.style.display = showCross ? '' : 'none';
    }
    if (showGlyph !== this._showGlyph) {
      this._showGlyph = showGlyph;
      this.glyph.style.display = showGlyph ? '' : 'none';
    }
    if (showEmitter !== this._showEmitter) {
      this._showEmitter = showEmitter;
      this.emitter.style.display = showEmitter ? '' : 'none';
    }

    const adrift = !at;
    const anchor = at ?? this._centre();
    if (adrift !== this._adrift) {
      this._adrift = adrift;
      this.el.classList.toggle('adrift', adrift);
      this.note.textContent = adrift ? 'no face' : '';
    }

    const { iod } = anchor;

    // Half a pixel is below anything visible and above what is left of the
    // landmark jitter after smoothing — the same guard the face view's type size
    // uses, for the same reason: these are the only layout writes here.
    if (showCross) {
      const size = iod * cfg.probe.SIZE;
      if (Math.abs(size - this._size) > 0.5) {
        this._size = size;
        this.cross.style.width = `${size.toFixed(1)}px`;
        this.cross.style.height = `${size.toFixed(1)}px`;
      }
    }

    if (showGlyph) {
      if (cfg.probe.LETTER !== this._letter) {
        this._letter = cfg.probe.LETTER;
        this.glyph.textContent = this._letter;
      }

      // The axes are the reason the glyph is here, so they are recomputed every
      // frame — but only written when they change, and only ever on this one
      // span. That is the difference between this and animating a whole line.
      const axes = variationSettings({
        ap: sample?.aperture ?? 0,
        wd: sample?.width ?? 0.5,
        grad: gradFor(audioFrame?.flatness),
      });
      if (axes !== this._axes) {
        this._axes = axes;
        this.glyph.style.fontVariationSettings = axes;
      }

      // Literally the function Emitter.emit sizes with, so what is parked here is
      // the size a letter leaves at rather than an approximation of it that can
      // drift the next time somebody tunes one of the two constants behind it.
      const type = glyphSize(iod, cfg.emit);
      if (Math.abs(type - this._type) > 0.5) {
        this._type = type;
        this.glyph.style.fontSize = `${type.toFixed(1)}px`;
      }

      // Held clear of the crosshair, in iod units like everything else, so the
      // glyph does not cover the thing it is there to help you look at. Two
      // translates rather than one: the second centres the glyph on its own
      // point, so an offset of zero is the letter *on* the lips rather than
      // hanging off to the right of them — and turning the crosshair off does
      // not make the letter jump.
      const gx = iod * cfg.probe.OFFSET;
      const gy = iod * cfg.probe.GLYPH_Y;
      this.glyph.style.transform = `translate(${gx.toFixed(1)}px, ${gy.toFixed(1)}px) translate(-50%, -50%)`;
    }

    // Where the type will actually come out. `origin` is the emitter's own
    // function with the randomness stubbed to zero, so this is the launch point
    // itself rather than a second opinion about it — and the ring around it is
    // the scatter, at the radius `emit.JITTER` will really use.
    if (showEmitter) {
      const from = origin(anchor, cfg.emit, () => 0);
      const ring = iod * cfg.emit.JITTER;
      if (Math.abs(ring - this._ring) > 0.5) {
        this._ring = ring;
        // The viewBox is 100 units across with the scatter drawn at r=48, so the
        // element is sized to twice the radius and the circle lands on it. A
        // floor keeps the launch dot visible when there is no scatter at all.
        const span = Math.max(ring * 2, iod * 0.12);
        this.emitter.style.width = `${span.toFixed(1)}px`;
        this.emitter.style.height = `${span.toFixed(1)}px`;
        this.scatter.style.display = ring > 0.5 ? '' : 'none';
      }
      this.emitter.style.transform =
        `translate(${(from.x - anchor.x).toFixed(1)}px, ${(from.y - anchor.y).toFixed(1)}px) ` +
        'translate(-50%, -50%)';
    }

    // The probe's own offset from the lip centroid, in iod so it holds as the
    // speaker moves. On the marker rather than on `this.el`, so that moving the
    // crosshair does not drag the launch ring along with it — they are answers to
    // different questions and are positioned by different controls.
    this.mark.style.transform = `translate(${(iod * cfg.probe.X).toFixed(1)}px, ${(iod * cfg.probe.Y).toFixed(1)}px)`;
    this.el.style.transform = `translate(${anchor.x.toFixed(1)}px, ${anchor.y.toFixed(1)}px)`;
  }
}

export default Probe;
